import { type TEAM_CATEGORY, USER_STATES } from 'cpool-constants';
import type { SchemaUserResolver_GetAdvisersByRole } from 'cpool-tables/backend-resolvers';
import type { AssignedUserSlot } from 'cpool-utils';
import { useTranslation } from 'react-i18next';

import { ActiveStar } from './ActiveStar';
import { AdviserSelect } from './AdviserSelect';
import { TeamCategoryIcon } from './TeamCategoryIcon';
import { cn } from '~/utils/cnTailwind';

type Props = {
    advisers: SchemaUserResolver_GetAdvisersByRole;
    disabled: boolean;
    holderName: string;
    onSelect: (userId: string) => void;
    selectedUserId: string;
    slot: AssignedUserSlot & { category: TEAM_CATEGORY };
};

export const ChangeAssignedUserRow: React.FC<Props> = ({
    advisers,
    disabled,
    holderName,
    onSelect,
    selectedUserId,
    slot,
}) => {
    const { t } = useTranslation();
    const teamOptions = advisers
        .filter((adviser) => !adviser.nameOnly && adviser.teamCategory === slot.category)
        .map((adviser) => ({
            disabled: adviser.id !== slot.userId && (adviser.state !== USER_STATES.AVAILABLE || adviser.absent),
            label: `${adviser.firstName} ${adviser.lastName}`,
            value: adviser.id,
        }));
    // current holder not among the offered options stays visible as the current value
    const options = teamOptions.some(({ value }) => value === slot.userId)
        ? teamOptions
        : [{ disabled: true, label: holderName, value: slot.userId }, ...teamOptions];
    const canChange = options.some((option) => option.value !== slot.userId && !option.disabled);
    const labelId = `change-assigned-user-${slot.category}`;

    return (
        <div className="contents">
            <span className="flex items-center gap-1 text-sm text-midnight" id={labelId}>
                <TeamCategoryIcon category={slot.category} />
                <span className={cn(slot.isActive && 'font-medium')}>{t(`TEAMS.CATEGORIES.${slot.category}`)}</span>
                {slot.isActive && <ActiveStar className="text-midnight" />}
            </span>
            <AdviserSelect
                aria-labelledby={labelId}
                data-testid={labelId}
                disabled={disabled || !canChange}
                onSelect={onSelect}
                options={options}
                value={selectedUserId}
            />
        </div>
    );
};
